import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { getClientDetails, submitHomeLoanEnquiry } from './asyncActions';
import type { RootState } from './store';
import type { CommonState, ReferenceDataState } from './types';

const initialState: CommonState = {
  isTablet: Boolean(window.innerWidth <= 768),
  enquiryFormType: 'GE',
  enquiryFormTypeRoute: 'general',
  utmCampaignCode: '',
  applicationType: '',
  referenceNumber: '',
  progress: [],
  errorBannerType: '',
  pageLoading: false,
  referenceData: {
    ageRange: [
      {
        label: 'Under 20',
        value: 'under-20',
      },
      {
        label: '20 - 30',
        value: '20-to-30',
      },
      {
        label: '31 - 40',
        value: '30-to-40',
      },
      {
        label: '41 - 54',
        value: '40-to-55',
      },
      {
        label: '55 - 64',
        value: '55-to-64',
      },
      {
        label: 'Over 65',
        value: 'over-65',
      },
    ],
    states: [
      {
        label: 'NSW',
        value: 'NSW',
      },
      {
        label: 'VIC',
        value: 'VIC',
      },
      {
        label: 'QLD',
        value: 'QLD',
      },
      {
        label: 'ACT',
        value: 'ACT',
      },
      {
        label: 'TAS',
        value: 'TAS',
      },
      {
        label: 'WA',
        value: 'WA',
      },
      {
        label: 'NT',
        value: 'NT',
      },
      {
        label: 'SA',
        value: 'SA',
      },
    ],
    yesOrNoOptions: [
      {
        value: 'true',
        label: 'Yes',
      },

      {
        value: 'false',
        label: 'No',
      },
    ],
    yesOrNoInvertedOptions: [
      {
        value: 'true',
        label: 'No',
      },
      {
        value: 'false',
        label: 'Yes',
      },
    ],
    newOrExistingCustomer: [
      {
        value: 'false',
        label: 'New',
      },
      {
        value: 'true',
        label: 'Existing',
      },
    ],
    isResident: [
      {
        value: 'true',
        label: 'Yes',
      },
      { value: 'false', label: 'No' },
    ],
    secureOrGeneralMessage: [
      {
        value: 'Secure',
        label: 'Send secure message',
      },
      {
        value: 'General',
        label: 'General enquiry',
      },
    ],
    homeLoanEnquiryTypeOptions: [
      {
        value: 'false',
        label: 'Apply online',
      },
      {
        value: 'true',
        label: 'Speak to a lender',
      },
    ],
    homeLoanApplicationTypesApplyOnline: [
      {
        value: 'Refinance',
        label: 'Refinance with us',
      },
      {
        value: 'Purchase',
        label: 'Purchase a new home',
      },
      {
        value: 'NewBuild',
        label: 'Build a new home',
      },
      {
        value: 'BridgingLoan',
        label: 'Get a bridging loan',
      },
    ],
    homeLoanApplicationTypes: [
      {
        value: 'Refinance',
        label: 'Refinance with us',
      },
      {
        value: 'Purchase',
        label: 'Purchase a new home',
      },
      {
        value: 'NewBuild',
        label: 'Build a new home',
      },
      {
        value: 'ManageExistingLoan',
        label: 'Manage my existing loan',
      },
      {
        value: 'IncreaseExistingLoan',
        label: 'Increase my existing loan',
      },
      {
        value: 'BridgingLoan',
        label: 'Get a bridging loan',
      },
    ],
    isPropertyOwnerOccupied: [
      {
        value: 'true',
        label: 'Yes, I will live in it',
      },
      {
        value: 'false',
        label: "No, it's an investment",
      },
    ],
    contactMethod: [
      {
        value: 'Phone',
        label: "I'd prefer a call",
      },
      {
        value: 'Email',
        label: "I'd prefer an email",
      },
    ],
    contactMethodGeneral: [
      {
        value: 'Email',
        label: 'Email',
      },
      {
        value: 'Phone',
        label: 'Phone',
      },
    ],
    employmentStatusOptions: [
      {
        value: 'Salary',
        label: 'Salary (e.g. earn a PAYG income)',
      },
      {
        value: 'SelfEmployed',
        label: 'Self-employed',
      },
      {
        value: 'NA',
        label: 'Other',
      },
    ],
    loanPurposeOptions: [
      {
        value: 'Car',
        label: 'Car',
      },
      {
        value: 'Motorbike',
        label: 'Motorbike',
      },
      {
        value: 'Caravan',
        label: 'Caravan',
      },
      {
        value: 'Boat',
        label: 'Boat',
      },
      {
        value: 'Renovations',
        label: 'Renovations',
      },
      {
        value: 'Consolidation',
        label: 'Consolidation',
      },
      {
        value: 'Holidays',
        label: 'Holidays',
      },
      {
        value: 'Other',
        label: 'Other',
      },
    ],
    housingSituationOptions: [
      {
        value: 'HomeOwner',
        label: 'Home owner',
      },
      {
        value: 'Renting',
        label: 'Renting',
      },
      {
        value: 'LivingWithParents',
        label: 'Living with parents',
      },
    ],
    personalLoanEmploymentStatusOptions: [
      {
        value: 'Salary',
        label: 'Salary (e.g. earn a PAYG income)',
      },
      {
        value: 'SelfEmployed',
        label: 'Self-employed',
      },
      {
        value: 'Casual',
        label: 'Casual',
      },
      {
        value: 'Retiree',
        label: 'Pensioner / self-funded retiree',
      },
      {
        value: 'NA',
        label: 'Other',
      },
    ],
    enquiryTypeOptions: [
      {
        label: 'Investing',
        value: 'Investment Planning',
      },
      {
        label: 'Retirement planning',
        value: 'Retirement Planning',
      },
      {
        label: 'Redundancy advice',
        value: 'Superannuation',
      },
      {
        label: 'Estate planning',
        value: 'Retirement Planning',
      },
      {
        label: 'Superannuation',
        value: 'Superannuation',
      },
      {
        label: 'Other',
        value: 'Other Enquiry',
      },
    ],
    enquiryFormTypes: [
      {
        value: 'GE',
        label: 'General enquiry',
        route: 'general',
      },
      {
        value: 'HL',
        label: 'Home Loan',
        route: 'home-loan',
      },
      {
        value: 'HLE',
        label: 'Home Loan Lender Enquiry',
        route: 'home-loan/lender-enquiry',
      },
      {
        value: 'HLAO',
        label: 'Home Loan Apply Online',
        route: 'home-loan/apply',
      },
      {
        value: 'PL',
        label: 'Personal Loan',
        route: 'personal-loan',
      },
      {
        value: 'HE',
        label: 'Hardship Enquiry',
        route: 'hardship',
      },
      {
        value: 'FP',
        label: 'Financial Planning',
        route: 'financial-planning',
      },
    ],
  },
};

export const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setReferenceData: (state, action: PayloadAction<ReferenceDataState>) => {
      state.referenceData = initialState.referenceData;
    },
    setIsTablet: (state, action: PayloadAction<boolean>) => {
      state.isTablet = action.payload;
    },
    setEnquiryFormType: (state, action: PayloadAction<string>) => {
      state.enquiryFormType = action.payload;
    },
    setEnquiryFormTypeRoute: (state, action: PayloadAction<string>) => {
      state.enquiryFormTypeRoute = action.payload;
    },

    setUtmCampaignCode: (state, action: PayloadAction<string>) => {
      state.utmCampaignCode = action.payload;
    },

    setApplicationType: (state, action: PayloadAction<string>) => {
      state.applicationType = action.payload;
    },
    setReferenceNumber: (state, action: PayloadAction<string>) => {
      state.referenceNumber = action.payload;
    },

    setErrorBannerType: (state, action: PayloadAction<string>) => {
      state.errorBannerType = action.payload;
    },
    setPageLoading: (state, action: PayloadAction<boolean>) => {
      state.pageLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // NOTE: Are one of these needed for each form? Assuming yes.
      .addCase(submitHomeLoanEnquiry.rejected, (state, action: any) => {
        if (action.payload === undefined || action.payload.status !== 400) {
          state.errorBannerType = 'SystemError';
        }
        state.pageLoading = false;
      })

      .addCase(getClientDetails.rejected, (state, action: any) => {
        if (action.payload === undefined || action.payload.status !== 400) {
          state.errorBannerType = 'SystemError';
        }
        state.pageLoading = false;
      });
  },
});

export const {
  setReferenceData,
  setIsTablet,
  setEnquiryFormType,
  setEnquiryFormTypeRoute,
  setUtmCampaignCode,
  setApplicationType,
  setReferenceNumber,
  setErrorBannerType,
  setPageLoading,
} = commonSlice.actions;

export const selectCommon = (state: RootState): CommonState => state.common;

export const selectPageTitle = (state: RootState): string => {
  return 'pageTitle';
};

export default commonSlice.reducer;
